/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import SuggestionsList from './suggestions-list';
import type { SearchSuggestions } from './types';

/**
 * Live BG caps the list at ten rows.
 *
 * EVIDENCE AND ITS LIMIT: `.reference/captures/bg-searchbar-m-375-b/App.tsx` —
 * the same panel as the empty capture with `dress` typed — carries exactly ten
 * `<li>`. Whether ten is a CAP or that query's coincidence is unevidenced from
 * one capture; a second typed capture returning a different count is what would
 * settle it. Capping is the conservative reading: it cannot make the panel
 * longer than anything live has been observed to render.
 */
const BG_SUGGESTION_LIMIT = 10;

interface BgSuggestionsProps {
    searchSuggestions: SearchSuggestions | null;
    closeAndNavigate: (link: string) => void;
}

/**
 * BG's suggestion list — the whole of what live BG puts under its search input.
 *
 * WHY THIS IS A SEPARATE LEAF AND NOT A BRANCH IN `suggestions-section.tsx`.
 * That file's entire job is an arrangement live BG does not have: section
 * headings ("Categories" / "Products" / "Popular Searches"), a product grid, a
 * "Did you mean" line, and two full layout branches. It already reads
 * `HEADER_COMPOSITION[brand]`, which makes it LOOK like the brand-aware place
 * to add a third arrangement — it is not. That lookup selects a BREAKPOINT;
 * both of its branches are NM's information architecture, and the file is
 * covered by NM's snapshots. A third branch there puts BG's structure inside a
 * file NM's next change silently moves.
 *
 * WHAT LIVE BG ACTUALLY RENDERS (`bg-searchbar-m-375-b/App.tsx`), every row of
 * which is load-bearing:
 *
 *   <ul>
 *     <li><a href="/search/">
 *           <span>zimmermann </span><span class="font-bold">dress</span><span>es</span>
 *         </a></li>
 *     … ten rows …
 *   </ul>
 *
 *   1. Query-phrase suggestions ONLY — no product tiles, no images, no prices,
 *      no categories, no thumbnails.
 *   2. No section heading. The `<ul>` is the first thing under the input.
 *   3. Rows are `<a href>`, not buttons.
 *   4. The matched substring is bold, split across three spans.
 *
 * DELIBERATELY NOT RENDERED, each an `[ours-extra]` the NM panel carries and
 * live BG demonstrably does not: recent searches, the shopper-agent card,
 * section headings, product tiles, row thumbnails, "Did you mean". The empty
 * capture's panel contains ZERO content below the input — blank white to the
 * bottom of the viewport — so an empty state here renders nothing at all.
 *
 * ONE THING THIS CANNOT SETTLE. The live panel has TWO sibling content slots;
 * with `dress` typed, slot A holds the `<ul>` and slot B is still empty. Slot B
 * is either dead markup or a product rail that this query did not fill. Only
 * slot A is built, because `dress` is about as product-rich a query as BG has
 * and it returned zero products. A typed capture on a different query — a
 * designer name is the likeliest — is what would change that.
 */
export default function BgSuggestions({
    searchSuggestions,
    closeAndNavigate,
}: BgSuggestionsProps): ReactElement | null {
    // Phrase suggestions first, popular searches after, deduplicated by target.
    // Both are query PHRASES, which is the only kind of row live BG shows; the
    // category and product arrays the same payload carries are not consulted.
    const rows = [
        ...(searchSuggestions?.phraseSuggestions ?? []),
        ...(searchSuggestions?.popularSearchSuggestions ?? []),
    ]
        .filter((row, index, all) => row.name && all.findIndex((other) => other.link === row.link) === index)
        .slice(0, BG_SUGGESTION_LIMIT);

    if (rows.length === 0) {
        return null;
    }

    return (
        <SuggestionsList
            suggestions={rows}
            searchPhrase={searchSuggestions?.searchPhrase}
            closeAndNavigate={closeAndNavigate}
            // Both are ADDITIVE, default-off props on the shared row primitive —
            // NM passes neither and renders exactly what it did before.
            as="link"
            highlight
        />
    );
}
